// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { TranslateService, TranslationKey } from '@tolgee/ngx';
import { Observable, combineLatest, forkJoin, from, mergeMap, of } from 'rxjs';
import { DEFAULT_LANGUAGE, FALLBACK_LANGUAGE as fallbackLanguage } from '../app.constants';
import { spacesToNonBreakingSpaces } from '../functions/spaces-to-non-breaking-spaces';
import { EnergyMetric } from '../model/energy-unit/energy-metric';
import { EnergyMetricLanguageMinimalLabel } from '../model/energy-unit/energy-metric-language-minimal-label';
import { Language } from '../model/enums/language';
import { LanguageLocale } from '../model/enums/language-locale';
import { WeightMetricLanguageLabel } from '../model/prefixed-unit/weight-metric-language-label';
import { WeightMetricLanguageMinimalLabel } from '../model/prefixed-unit/weight-metric-language-minimal-label';
import { WeightMetric } from '../model/prefixed-unit/weight.metric';
import { WeightUSMetric } from '../model/prefixed-unit/weight.us-metric';
import { TranslatableItem, isTranslatableItem } from '../model/translatable-item';
import { LanguageService } from './language.service';

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private _defaultLanguage: Language = DEFAULT_LANGUAGE;
  private _localeList: { [key in Language]: LanguageLocale } = {
    [Language.SWEDISH]: LanguageLocale.sv_SE,
    [Language.SWEDISH_SE]: LanguageLocale.sv_SE,
    [Language.SWEDISH_PA]: LanguageLocale.sv_PA,
    [Language.ENGLISH]: LanguageLocale.en_GB,
    [Language.ENGLISH_GB]: LanguageLocale.en_GB,
    [Language.ENGLISH_PA]: LanguageLocale.en_PA,
    [Language.ENGLISH_US]: LanguageLocale.en_US,
    [Language.SPANISH]: LanguageLocale.es_ES,
    [Language.SPANISH_ES]: LanguageLocale.es_ES,
    [Language.GERMAN]: LanguageLocale.de_DE,
    [Language.GERMAN_DE]: LanguageLocale.de_DE,
    [Language.GERMAN_CH]: LanguageLocale.de_CH,
    [Language.FRENCH]: LanguageLocale.fr_FR,
    [Language.FRENCH_FR]: LanguageLocale.fr_FR,
    [Language.DUTCH]: LanguageLocale.nl_NL,
    [Language.DUTCH_NL]: LanguageLocale.nl_NL,
  };
  private _translations: { [key in LanguageLocale]: Object } | {} = {};

  get translations() {
    return this._translations;
  }

  constructor(
    private _languageService: LanguageService,
    private _tolgee: TranslateService
  ) {}

  /**
   * Used when the indication of the percent is a non-zero value that might be rounded down to 0 and displaying 0 is not desirable.
   * Formats with non-breaking spaces.
   * @param language
   * @param percentValue
   * @returns
   */
  formatAsAboveZeroPercent(language: Language, percentValue: number): string {
    if (percentValue < 1) {
      return this._replacePercentValue(language, '< 1');
    }
    return this.formatAsNonBreakingPercent(language, percentValue);
  }

  formatAsNonBreakingPercent(language: Language, percentValue: number): string {
    return spacesToNonBreakingSpaces(this.formatAsPercent(language, percentValue));
  }

  formatAsNumericRange(startValue: number, endValue: number): string {
    //TODO: should probably be using a style guide to decide the way this is formatted.
    return `${startValue}-${endValue}`;
  }
  /**
   *
   * @param language
   * @param percentValue a number denoting a percent. 100 is 100%.
   * @returns
   */
  formatAsPercent(language: Language, percentValue: number): string {
    const value = percentValue / 100;
    const locale = this.getLocale(language);
    const formatter = new Intl.NumberFormat(locale, { style: 'percent' });
    return formatter.format(value);
  }

  formatAsPercentWithUserLocale(percentValue: number) {
    return this.formatAsPercent(this._languageService.language, percentValue);
  }

  formatToPartsWithCompactNotation(
    language: Language,
    value: number,
    maximumDecimals = 1,
    compactDisplay: 'short' | 'long' = 'short'
  ): { type: string; value: string }[] {
    const locale = this.getLocale(language);
    const formatter = new Intl.NumberFormat(locale, {
      maximumFractionDigits: maximumDecimals,
      //@ts-ignore
      notation: 'compact',
      compactDisplay,
    });
    return formatter.formatToParts(value);
  }

  formatWithCompactNotation(
    language: Language,
    value: number,
    maximumDecimals = 1,
    compactDisplay: 'short' | 'long' = 'short'
  ): string {
    const locale = this.getLocale(language);
    const formatter = new Intl.NumberFormat(locale, {
      maximumFractionDigits: maximumDecimals,
      //@ts-ignore
      notation: 'compact',
      compactDisplay,
    });
    return formatter.format(value);
  }

  /**
   * Creates a string with the value + unit. The value will be displayed as is, i.e. it is expected to be in the given unit.
   * @param language
   * @param value
   * @param unit
   * @param compactDisplay
   */
  formatWithUnit(
    language: Language,
    value: string,
    unit: WeightMetric | WeightUSMetric,
    compactDisplay: 'short' | 'long' = 'short'
  ): string {
    const suffix = this.translateWeightMetric(unit, language, compactDisplay);
    return `${value} ${suffix}`;
  }

  getLocale(language: Language): LanguageLocale {
    let locale = this._localeList[language];
    if (!locale) {
      locale = this._localeList[this._defaultLanguage];
    }
    return locale;
  }

  getLocalisedStringCompare(language: Language): (a: string, b: string) => number {
    return (a: string, b: string) => {
      return a.localeCompare(b, language);
    };
  }
  getSupportLanguage(language: Language): Language {
    if (language === Language.SWEDISH) {
      return Language.SWEDISH;
    }
    return DEFAULT_LANGUAGE;
  }

  inlineEllipsis(): string {
    return '…';
  }

  isUserOrDefaultLanguageLoaded(): boolean {
    return !!this._translations[this._languageService.language] || !!this._translations[LanguageLocale.en_GB];
  }

  loadLanguageIfNeeded(language: Language, namespace?: string): Observable<any> {
    const load$: Observable<void> = from(this._loadLanguage(language, namespace));
    const fallback = fallbackLanguage[language];
    if (fallback) {
      return forkJoin([load$, this.loadLanguageIfNeeded(fallback, namespace)]);
    }
    return load$;
  }

  toLocaleDateString(language: Language, date: Date): string {
    const locale = this.getLocale(language);
    return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  }

  toLowercase(language: Language, text: string): string {
    const locale = this.getLocale(language);
    return (text as any).toLocaleLowerCase(locale);
  }

  toUppercase(language: Language, text: string): string {
    if (language == null) {
      //Catches both undefined and null
      language = this._defaultLanguage;
    }
    const locale = this.getLocale(language);
    //Note: this is listed as incompletely supported by most browsers at the time of writing.
    //We have it mostly as a placeholder, there is a backlog item for this that needs to be resolved to support new languages /Emanuel.
    return (text as any).toLocaleUpperCase(locale);
  }

  /**
   * @deprecated Prefer to use the translate-string pipe, or the observable version of this method instead if possible.
   */
  translate(language: Language, translationKey: TranslationKey | string, ...params: (string | number)[]): string;
  translate({
    language,
    key,
    params,
  }: {
    key: TranslationKey;
    language?: Language;
    ns?: string;
    params?: { [key: string]: string | number };
  }): string;
  translate(...data: any[]): string {
    let props: { key: TranslationKey; language?: Language; params?: { [key: string]: string | number } };

    if (data.length === 1 && typeof data[0] === 'object') {
      props = data[0];
    } else {
      props = {
        language: data[0] as Language | undefined,
        key: data[1] as TranslationKey,
        params: { legacy: JSON.stringify(data.slice(2)) },
      };
    }

    if (props.language) {
      this._loadLanguage(props.language);
    }

    return this._tolgee.instant(props) ?? '';
  }

  translate$({
    language,
    translationKey,
    params,
  }: {
    translationKey: TranslationKey;
    language?: Language;
    params?: { [key: string]: string | number };
  }): Observable<string> {
    if (language) {
      this._loadLanguage(language);
    }
    return this._tolgee.translate(translationKey, { ...params, language });
  }

  translateEnergyMetric(unit: EnergyMetric, language: Language): string {
    const key = EnergyMetricLanguageMinimalLabel[unit];
    return this.translate(language, key);
  }

  translateNested$({
    language,
    translatableItem,
  }: {
    language?: Language;
    translatableItem: TranslatableItem;
  }): Observable<string> {
    if (translatableItem.params) {
      const paramKeys = Object.keys(translatableItem.params);
      const paramObservables = paramKeys.map((paramKey) => {
        const param = translatableItem.params![paramKey];
        if (isTranslatableItem(param)) {
          return this.translateNested$({ language, translatableItem: param });
        } else {
          return of(param);
        }
      });
      return combineLatest(paramObservables).pipe(
        mergeMap((paramResults) => {
          const params: { [key: string]: string | number } = {};
          paramResults.forEach((paramResult, index) => {
            params[paramKeys[index]] = paramResult;
          });
          return this.translate$({
            language,
            translationKey: translatableItem.key,
            params,
          });
        })
      );
    }
    return this.translate$({
      language,
      translationKey: translatableItem.key,
    });
  }

  /**
   * @deprecated
   */
  translateToUserLanguage(stringId: string, ...params: (string | number)[]): string;
  translateToUserLanguage(stringId: TranslationKey, ...params: (string | number)[]): string {
    this._loadLanguage(this._languageService.language);
    return this.translate(this._languageService.language, stringId, ...params);
  }

  translateWeightMetric(
    unit: WeightMetric | WeightUSMetric,
    language: Language,
    compactDisplay: 'short' | 'long'
  ): string {
    const key = compactDisplay === 'long' ? WeightMetricLanguageLabel[unit] : WeightMetricLanguageMinimalLabel[unit];
    return this.translate(language, key);
  }

  usesTitleCase(language: Language): boolean {
    //should return true for en-US, not en-GB
    return language === Language.ENGLISH_US ? true : false;
  }

  quote(text: string): string {
    return '"' + text + '"';
  }

  private _loadLanguage(lang: Language, namespace?: string): Promise<void> {
    const isLangPending = this._tolgee.tolgee.getPendingLanguage() === lang;
    const isLanguageLoaded =
      this._tolgee.tolgee.getRecord({ language: lang }) &&
      (namespace ? this._tolgee.tolgee.getRecord({ language: lang, namespace }) : true);
    if (!isLanguageLoaded && !isLangPending) {
      const recordsToLoad = [this._tolgee.tolgee.loadRecord({ language: lang })];
      if (namespace) {
        recordsToLoad.push(this._tolgee.tolgee.loadRecord({ language: lang, namespace }));
      }
      return Promise.all(recordsToLoad).then();
    } else {
      return Promise.resolve();
    }
  }

  private _replacePercentValue(language: Language, percentValue: string): string {
    const formatted = this.formatAsNonBreakingPercent(language, 0);
    //We must replace, in Turkish the percent-sign is first, this might be true for other languages as well.
    return formatted.replace('0', percentValue);
  }
}
